import { ChangeDetectorRef, Component, OnInit } from '@angular/core'
import { GlobalMessageType, TranslationService } from '@spartacus/core'
import { combineLatest } from 'rxjs'
import { delay, finalize, switchMap, tap } from 'rxjs/operators'
import { IHistoryList } from '../../../models/interface/history-list.interface'
import { ISharedPagination } from '../../../models/interface/shared.interface'
import { CustomCartService } from '../../../services/custom-cart.service'
import { OrderHistoryService } from '../../../services/order-history.service'
import { CustomGlobalMessageService } from '../../../services/shared/custom-global-message/custom-global-message.service'
import { CustomModalService } from '../../../services/shared/modal/custom-modal.service'
import { ConfirmModalComponent, DataConfirmModal } from '../../shared/confirm-modal/confirm-modal.component'
import { ICustomSelectOption } from '../../shared/custom-components/custom-select/modals/interface/custom-select.interface'
import { CustomTableColumnType } from '../../shared/custom-components/custom-table/modals/enum/custom-table.enum'
import {
  ICustomTableColumn,
  ICustomTableHeader
} from '../../shared/custom-components/custom-table/modals/interface/custom-table.interface'

@Component({
    selector: 'app-saved-carts',
    templateUrl: './saved-carts.component.html',
    styleUrls: ['./saved-carts.component.scss'],
    standalone: false
})
export class SavedCartsComponent implements OnInit {
  public list: IHistoryList
  public showLoaderOnPagination: boolean
  public showInitialLoader = true
  public selectedSort: string

  public sortOptions: ICustomSelectOption[] = [
    { label: 'shared.sort.byDateCreated', value: 'byDateSaved' },
    { label: 'shared.sort.byName', value: 'byName' },
    { label: 'shared.sort.byCode', value: 'byCode' },
    { label: 'shared.sort.byTotal', value: 'byTotal' }
  ]

  public headers: ICustomTableHeader[] = [
    { title: 'importCart.code' },
    { title: 'importCart.dateCreated' },
    { title: 'shared.name' },
    { title: 'importCart.totalPrice' },
    { title: '', type: CustomTableColumnType.BUTTON, cssStyle: 'min-width: 70px' }
  ]

  public columns: ICustomTableColumn[] = [
    { key: 'code' },
    { key: 'saveTime', type: CustomTableColumnType.DATE },
    { key: 'name' },
    {
      key: 'totalPrice.formattedValue',
      type: CustomTableColumnType.SAVED_CART_PRICE,
      button: {
        icon: 'fas fa-sync',
        handler: () => this.refreshPageData()
      }
    },
    {
      key: 'restore',
      type: CustomTableColumnType.BUTTON,
      button: {
        title: 'importCart.restore',
        translate: true,
        handler: (res) => this.restoreCart(res)
      }
    }
  ]

  constructor(
    private cartService: CustomCartService,
    private changeDetectorRef: ChangeDetectorRef,
    private globalMessageService: CustomGlobalMessageService,
    private translation: TranslationService,
    private customModalService: CustomModalService,
    private customCartService: CustomCartService,
    private orderHistoryService: OrderHistoryService
  ) {}

  ngOnInit(): void {
    this.cartService
      .getSavedCarts()
      .pipe(
        finalize(() => {
          this.showInitialLoader = false
          this.changeDetectorRef.detectChanges()
        })
      )
      .subscribe(
        (res) => {
          this.list = res
        },
        (err) => {
          this.globalMessageService.add(err.message, GlobalMessageType.MSG_TYPE_ERROR)
        }
      )
  }

  public restoreCart(value: any): void {
    combineLatest([
      this.translation.translate('modals.restoreCart.title'),
      this.translation.translate('modals.restoreCart.msg'),
      this.translation.translate('modals.restoreCart.extra'),
      this.translation.translate('modals.restoreCart.yes')
    ]).subscribe(([title, msg, extra, yes]) => {
      const dataModal: DataConfirmModal = { title, msg, extra, yes }
      this.customModalService.showConfirmModal(ConfirmModalComponent, dataModal).then((ok) => {
        if (!ok) {
          return
        }

        this.showLoaderOnPagination = true
        this.changeDetectorRef.detectChanges()
        this.cartService
          .restoreCart(value?.code)
          .pipe(
            switchMap(() => {
              return this.customCartService.getCurrentCart().pipe(
                tap((cart) => this.orderHistoryService.loadReorderedCart(cart?.code)),
                delay(1000)
              )
            })
          )
          .subscribe(
            () => {
              this.refreshPageData()
            },
            (err) => {
              this.showLoaderOnPagination = false
              this.changeDetectorRef.detectChanges()
              this.globalMessageService.add(err.message, GlobalMessageType.MSG_TYPE_ERROR)
            }
          )
      })
    })
  }

  public sortChange(value: string): void {
    this.selectedSort = value
    this.changePageData({
      sort: value
    })
  }

  public pageChange(page: number): void {
    this.changePageData({
      pageNumber: page,
      sort: this.selectedSort
    })
  }

  public changePageData(params: ISharedPagination): void {
    this.showLoaderOnPagination = true
    this.cartService
      .getSavedCarts(params)
      .pipe(
        finalize(() => {
          this.showLoaderOnPagination = false
          this.changeDetectorRef.detectChanges()
        })
      )
      .subscribe(
        (res) => {
          this.list = res
        },
        (err) => {
          this.globalMessageService.add(err.message, GlobalMessageType.MSG_TYPE_ERROR)
        }
      )
  }

  public refreshPageData(): void {
    this.changePageData({
      sort: this.selectedSort
    })
  }
}
